import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, GripVertical, Briefcase, GraduationCap, Trophy, Award, Calendar, MapPin, Eye, EyeOff, Save, X, ChevronDown, ChevronUp, ExternalLink, Check, Link2, Copy } from 'lucide-react';
import { databaseService, storageService, ID } from '../../../lib/appwrite';
import { useToast } from '../../../contexts/ToastContext';
import RichTextEditor from '../../../components/RichTextEditor/RichTextEditor';

const EXPERIENCES_COLLECTION = 'experiences';
const EXPERIENCE_BUCKET = 'experience_images';

// Experience types with icons
const experienceTypes = [
  { value: 'work', label: 'Work Experience', icon: Briefcase },
  { value: 'education', label: 'Education', icon: GraduationCap },
  { value: 'achievement', label: 'Achievement', icon: Trophy },
  { value: 'award', label: 'Award', icon: Award },
];

// Preset colors
const presetColors = [
  { primary: '#4C1D95', secondary: '#1E8479', name: 'Deep Purple' },
  { primary: '#1e40af', secondary: '#3b82f6', name: 'Ocean Blue' },
  { primary: '#7c3aed', secondary: '#a78bfa', name: 'Violet' },
  { primary: '#dc2626', secondary: '#f87171', name: 'Red' },
  { primary: '#ea580c', secondary: '#fb923c', name: 'Orange' },
  { primary: '#16a34a', secondary: '#4ade80', name: 'Green' },
  { primary: '#0891b2', secondary: '#22d3ee', name: 'Cyan' },
  { primary: '#be185d', secondary: '#f472b6', name: 'Pink' },
  { primary: '#111827', secondary: '#374151', name: 'Dark' },
];

const emptyExperience = {
  title: '',
  organization: '',
  type: 'achievement',
  description: '',
  location: '',
  startDate: '',
  endDate: '',
  current: false,
  skills: [],
  highlights: [],
  color: '#4C1D95',
  colorSecondary: '#1E8479',
  imageUrl: '',
  credentialUrl: '',
  featured: true,
  order: 0,
};

export default function ExperienceManagement() {
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingExp, setEditingExp] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState(emptyExperience);
  const [skillInput, setSkillInput] = useState('');
  const [highlightInput, setHighlightInput] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const { showToast } = useToast();

  useEffect(() => {
    loadExperiences();
  }, []);

  const loadExperiences = async () => {
    try {
      const result = await databaseService.listDocuments(EXPERIENCES_COLLECTION);
      if (result.success) {
        const sorted = result.data.documents.sort((a, b) => (a.order || 0) - (b.order || 0));
        setExperiences(sorted);
      }
    } catch (error) {
      console.error('Error loading experiences:', error);
      showToast('Failed to load experiences', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (exp = null) => {
    if (exp) {
      setEditingExp(exp);
      setFormData({
        ...emptyExperience,
        ...exp,
        skills: exp.skills || [],
        highlights: exp.highlights || [],
        startDate: exp.startDate || '',
        endDate: exp.endDate || '',
        imageUrl: exp.imageUrl || '',
        credentialUrl: exp.credentialUrl || '',
      });
      setImagePreview(exp.imageUrl || '');
    } else {
      setEditingExp(null);
      setFormData({ ...emptyExperience, order: experiences.length });
      setImagePreview('');
    }
    setImageFile(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingExp(null);
    setFormData(emptyExperience);
    setImageFile(null);
    setImagePreview('');
    setSkillInput('');
    setHighlightInput('');
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const addSkill = () => {
    if (skillInput.trim() && !formData.skills.includes(skillInput.trim())) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, skillInput.trim()]
      }));
      setSkillInput('');
    }
  };

  const removeSkill = (skill) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(s => s !== skill)
    }));
  };

  const addHighlight = () => {
    if (highlightInput.trim()) {
      setFormData(prev => ({
        ...prev,
        highlights: [...prev.highlights, highlightInput.trim()]
      }));
      setHighlightInput('');
    }
  };

  const removeHighlight = (index) => {
    setFormData(prev => ({
      ...prev,
      highlights: prev.highlights.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      showToast('Title is required', 'error');
      return;
    }

    setSaving(true);
    try {
      let imageUrl = formData.imageUrl;

      // Upload image if new file selected
      if (imageFile) {
        const fileId = ID.unique();
        const uploadResult = await storageService.uploadFile(EXPERIENCE_BUCKET, fileId, imageFile);
        if (uploadResult.success) {
          imageUrl = storageService.getFileView(EXPERIENCE_BUCKET, uploadResult.data.$id);
        }
      }

      const expData = {
        title: formData.title.trim(),
        organization: formData.organization.trim(),
        type: formData.type,
        description: formData.description.trim(),
        location: formData.location.trim(),
        startDate: formData.startDate || '',
        endDate: formData.current ? '' : (formData.endDate || ''),
        current: formData.current,
        skills: formData.skills,
        highlights: formData.highlights,
        color: formData.color,
        colorSecondary: formData.colorSecondary,
        imageUrl: imageUrl || '',
        credentialUrl: formData.credentialUrl.trim(),
        featured: formData.featured,
        order: formData.order,
      };

      if (editingExp) {
        // Update existing
        const result = await databaseService.updateDocument(
          EXPERIENCES_COLLECTION,
          editingExp.$id,
          expData
        );
        if (result.success) {
          showToast('Experience updated successfully', 'success');
          loadExperiences();
          closeModal();
        } else {
          throw new Error(result.error);
        }
      } else {
        // Create new
        const result = await databaseService.createDocument(
          EXPERIENCES_COLLECTION,
          expData
        );
        if (result.success) {
          showToast('Experience created successfully', 'success');
          loadExperiences();
          closeModal();
        } else {
          throw new Error(result.error);
        }
      }
    } catch (error) {
      console.error('Error saving experience:', error);
      showToast('Failed to save experience', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (exp) => {
    if (!window.confirm(`Delete "${exp.title}"? This cannot be undone.`)) return;

    try {
      const result = await databaseService.deleteDocument(
        EXPERIENCES_COLLECTION,
        exp.$id
      );
      if (result.success) {
        showToast('Experience deleted', 'success');
        loadExperiences();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error deleting experience:', error);
      showToast('Failed to delete experience', 'error');
    }
  };

  const toggleFeatured = async (exp) => {
    try {
      const result = await databaseService.updateDocument(
        EXPERIENCES_COLLECTION,
        exp.$id,
        { featured: !exp.featured }
      );
      if (result.success) {
        showToast(`Experience ${!exp.featured ? 'featured' : 'hidden'}`, 'success');
        loadExperiences();
      }
    } catch (error) {
      console.error('Error toggling featured:', error);
      showToast('Failed to update', 'error');
    }
  };

  const moveExperience = async (index, direction) => {
    const newExperiences = [...experiences];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newExperiences.length) return;

    // Swap
    [newExperiences[index], newExperiences[targetIndex]] = [newExperiences[targetIndex], newExperiences[index]];

    // Optimistic Update
    setExperiences(newExperiences);

    // Update order values in background
    try {
      await Promise.all([
        databaseService.updateDocument(EXPERIENCES_COLLECTION, newExperiences[index].$id, { order: index }),
        databaseService.updateDocument(EXPERIENCES_COLLECTION, newExperiences[targetIndex].$id, { order: targetIndex })
      ]);
    } catch (error) {
      console.error('Error reordering:', error);
      showToast('Failed to sync order', 'error');
      loadExperiences(); // Revert on failure
    }
  };

  const getTypeIcon = (type) => {
    const found = experienceTypes.find(t => t.value === type);
    return found ? found.icon : Award;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#4C1D95]">Experience Management</h1>
          <p className="text-gray-500 mt-1">Showcase your professional journey and milestones</p>
        </div>
        <button
          onClick={() => openModal()}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#4C1D95] text-white rounded-xl hover:bg-[#3b1575] transition-all shadow-md hover:shadow-lg active:scale-95 font-medium"
        >
          <Plus className="w-5 h-5" />
          Add Experience
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#4C1D95]"></div>
        </div>
      ) : experiences.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl border-2 border-dashed border-gray-200">
          <div className="w-20 h-20 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <Trophy className="w-10 h-10 text-[#4C1D95]" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">No experiences yet</h3>
          <p className="text-gray-500 mb-8 max-w-sm mx-auto">Add your first work experience, education, or achievement to build your profile.</p>
          <button
            onClick={() => openModal()}
            className="inline-flex items-center gap-2 px-8 py-3 bg-[#4C1D95] text-white rounded-xl hover:bg-[#3b1575] transition-colors font-medium"
          >
           <Plus className="w-5 h-5" />
           Add First Experience
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {experiences.map((exp, index) => {
            const TypeIcon = getTypeIcon(exp.type);
            return (
              <div
                key={exp.$id}
                className="group bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col"
                style={{
                  border: '1px solid rgba(0,0,0,0.08)'
                }}
              >
                {/* macOS Window Header */}
                <div className="bg-[#F8FBFE] px-3 py-2 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-[#ff605c]"></span>
                    <span className="w-2 h-2 rounded-full bg-[#ffbd44]"></span>
                    <span className="w-2 h-2 rounded-full bg-[#00ca4e]"></span>
                  </div>
                  <div 
                    className="px-2 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-wider"
                    style={{ 
                      backgroundColor: `${exp.color || '#4C1D95'}15`,
                      color: exp.color || '#4C1D95'
                    }}
                  >
                    {exp.type}
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-3 flex-1 flex flex-col">
                  {/* Image */}
                  {exp.imageUrl && (
                    <div 
                      className="aspect-[4/3] bg-gray-50 rounded-lg overflow-hidden border border-gray-100 mb-3"
                      style={{
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                      }}
                    >
                      <img
                        src={exp.imageUrl}
                        alt={exp.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  {/* Title */}
                  <h3 
                    className="font-bold text-sm mb-1 line-clamp-2 leading-tight"
                    style={{ color: exp.color || '#4C1D95' }}
                  >
                    {exp.title}
                  </h3>

                  {/* Organization */}
                  <p className="text-xs text-gray-600 mb-2 line-clamp-1">
                    {exp.organization || 'Independent'}
                  </p>

                  {/* Date & Location */}
                  <div className="flex flex-wrap gap-2 text-[10px] text-gray-500 mb-3">
                    {exp.location && (
                      <span className="flex items-center gap-0.5">
                        <MapPin className="w-2.5 h-2.5" /> {exp.location}
                      </span>
                    )}
                    {exp.startDate && (
                      <span className="flex items-center gap-0.5">
                        <Calendar className="w-2.5 h-2.5" />
                        {new Date(exp.startDate).getFullYear()}
                        {exp.current ? ' - Present' : (exp.endDate ? ` - ${new Date(exp.endDate).getFullYear()}` : '')}
                      </span>
                    )}
                  </div>

                  {/* Skills Tags */}
                  {exp.skills && exp.skills.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {exp.skills.slice(0, 2).map((skill, i) => (
                        <span key={i} className="px-1.5 py-0.5 bg-gray-50 text-gray-600 text-[8px] font-semibold rounded border border-gray-100 uppercase tracking-wide">
                          {skill}
                        </span>
                      ))}
                      {exp.skills.length > 2 && (
                        <span className="px-1.5 py-0.5 text-gray-400 text-[8px] font-semibold">+ {exp.skills.length - 2}</span>
                      )}
                    </div>
                  )}

                  <div className="mt-auto pt-3 border-t border-gray-50 flex items-center justify-between gap-1.5">
                     {/* Move Controls */}
                     <div className="flex items-center bg-gray-50 rounded p-0.5">
                         <button 
                            onClick={(e) => { e.stopPropagation(); moveExperience(index, 'up'); }}
                            disabled={index === 0}
                            className="p-1 hover:bg-white text-gray-400 hover:text-gray-700 rounded disabled:opacity-30 transition-all"
                         >
                            <ChevronUp className="w-3 h-3" />
                         </button>
                         <button 
                            onClick={(e) => { e.stopPropagation(); moveExperience(index, 'down'); }}
                            disabled={index === experiences.length - 1}
                            className="p-1 hover:bg-white text-gray-400 hover:text-gray-700 rounded disabled:opacity-30 transition-all"
                         >
                            <ChevronDown className="w-3 h-3" />
                         </button>
                     </div>

                     <div className="flex items-center gap-1">
                         <button
                           onClick={() => toggleFeatured(exp)}
                           className={`p-1.5 rounded-lg transition-all ${
                             exp.featured 
                               ? 'bg-green-50 text-green-600 hover:bg-green-100' 
                               : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                           }`}
                           title={exp.featured ? 'Featured on Homepage' : 'Hidden from Homepage'}
                         >
                           {exp.featured ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                         </button>
                         <button 
                           onClick={() => {
                             const shareUrl = `${window.location.origin}/about?exp=${exp.$id}#experience`;
                             navigator.clipboard.writeText(shareUrl);
                             showToast('Experience link copied!', 'success');
                           }}
                           className="p-1.5 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors"
                           title="Copy Share Link"
                         >
                           <Link2 className="w-3 h-3" />
                         </button>
                         <button onClick={() => openModal(exp)} className="p-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors">
                           <Edit2 className="w-3 h-3" />
                         </button>
                         <button onClick={() => handleDelete(exp)} className="p-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors">
                           <Trash2 className="w-3 h-3" />
                         </button>
                     </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 sm:p-6 md:p-10 overflow-hidden">
          <div className="bg-white rounded-3xl w-full max-w-4xl max-h-full flex flex-col shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300 ring-1 ring-black/5">
            {/* Header - Sticky */}
            <div className="flex items-center justify-between p-5 border-b bg-gray-50/80 backdrop-blur-md sticky top-0 z-10">
              <div>
                <h2 className="text-xl font-bold text-gray-800">{editingExp ? 'Edit Experience' : 'New Experience'}</h2>
                <p className="text-[11px] text-[#4C1D95] font-bold uppercase tracking-widest mt-0.5 opacity-70">Professional Journey</p>
              </div>
              <button 
                onClick={closeModal} 
                className="p-2 hover:bg-gray-200 rounded-full transition-all text-gray-400 hover:text-gray-600 hover:rotate-90"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
              <div className="p-8 overflow-y-auto flex-1 min-h-0 space-y-8 scroll-smooth custom-scrollbar">
                 {/* Basic Info */}
                 <div className="space-y-4">
                    <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wider mb-4 border-b pb-2">Experience Details</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="col-span-1 md:col-span-2">
                           <label className="block text-sm font-bold text-gray-700 mb-1.5">Title <span className="text-red-500">*</span></label>
                           <input
                             type="text"
                             value={formData.title}
                             onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                             className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all"
                             placeholder="e.g. Senior Product Designer"
                             required
                           />
                        </div>

                        <div>
                           <label className="block text-sm font-bold text-gray-700 mb-1.5">Organization</label>
                           <input
                             type="text"
                             value={formData.organization}
                             onChange={(e) => setFormData(prev => ({ ...prev, organization: e.target.value }))}
                             className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all"
                             placeholder="e.g. Apple Inc."
                           />
                        </div>

                        <div>
                           <label className="block text-sm font-bold text-gray-700 mb-1.5">Location</label>
                           <input
                             type="text"
                             value={formData.location}
                             onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                             className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all"
                             placeholder="e.g. Cupertino, CA"
                           />
                        </div>
                    </div>
                 </div>

                 {/* Type & Timeline */}
                 <div className="space-y-4">
                    <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wider mb-4 border-b pb-2">Timeline & Type</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                           <label className="block text-sm font-bold text-gray-700 mb-1.5">Experience Type</label>
                           <select
                             value={formData.type}
                             onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                             className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all"
                           >
                             {experienceTypes.map(type => (
                               <option key={type.value} value={type.value}>{type.label}</option>
                             ))}
                           </select>
                        </div>

                        <div>
                           <label className="block text-sm font-bold text-gray-700 mb-1.5">Start Date</label>
                           <input
                             type="date"
                             value={formData.startDate}
                             onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                             className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all"
                           />
                        </div>

                        <div>
                           <label className="block text-sm font-bold text-gray-700 mb-1.5">End Date</label>
                           <div className="space-y-2">
                             <input
                               type="date"
                               value={formData.endDate}
                               onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                               disabled={formData.current}
                               className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                             />
                             <label className="flex items-center gap-2 cursor-pointer">
                               <div className="relative inline-flex items-center">
                                 <input
                                   type="checkbox"
                                   checked={formData.current}
                                   onChange={(e) => setFormData(prev => ({ ...prev, current: e.target.checked }))}
                                   className="sr-only peer"
                                 />
                                 <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#4C1D95]"></div>
                               </div>
                               <span className="text-sm font-medium text-gray-600">Currently Ongoing</span>
                             </label>
                           </div>
                        </div>
                    </div>
                 </div>

                 {/* Image Upload - Moved up for better visibility */}
                 <div className="space-y-4">
                   <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wider mb-4 border-b pb-2">Experience Image</h3>
                   
                   <div>
                      <div className="space-y-3">
                        {imagePreview && (
                          <div className="relative w-full h-48 rounded-xl overflow-hidden border-2 border-gray-200 bg-gray-50">
                            <img 
                              src={imagePreview} 
                              alt="Preview" 
                              className="w-full h-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setImageFile(null);
                                setImagePreview('');
                                setFormData(prev => ({ ...prev, imageUrl: '' }));
                              }}
                              className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors shadow-lg"
                              title="Remove image"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                        <div className="flex items-center gap-3">
                          <label className="flex-1 cursor-pointer">
                            <div className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl hover:border-[#4C1D95] hover:bg-purple-50 transition-all">
                              <Plus className="w-5 h-5 text-gray-400" />
                              <span className="text-sm font-medium text-gray-600">
                                {imagePreview ? 'Change Image' : 'Upload Image'}
                              </span>
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleImageChange}
                              className="hidden"
                            />
                          </label>
                        </div>
                        <p className="text-xs text-gray-500">Recommended: 1200x630px, max 5MB (JPG, PNG, WebP)</p>
                      </div>
                   </div>
                 </div>

                 {/* Rich Content */}
                 <div className="space-y-4">
                   <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wider mb-4 border-b pb-2">Description & Skills</h3>
                   
                   <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1.5">Responsibilities & Achievements</label>
                      <div className="rounded-xl overflow-hidden border border-gray-200 focus-within:ring-2 focus-within:ring-[#4C1D95]/20 transition-all">
                        <RichTextEditor
                          value={formData.description}
                          onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                          placeholder="What did you accomplish here?"
                          minHeight="150px"
                        />
                      </div>
                   </div>

                   {/* Skills Input */}
                   <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1.5">Skills Used</label>
                      <div className="flex gap-2 mb-3">
                        <input
                          type="text"
                          value={skillInput}
                          onChange={(e) => setSkillInput(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                          className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all"
                          placeholder="Add skills (e.g. React, Leadership, Project Management)..."
                        />
                        <button
                          type="button"
                          onClick={addSkill}
                          className="px-6 py-2.5 bg-gray-800 text-white font-bold rounded-xl hover:bg-gray-900 transition-colors"
                        >
                          Add
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2 min-h-[40px] p-2 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                        {formData.skills.length === 0 && <span className="text-sm text-gray-400 italic">No skills added yet</span>}
                        {formData.skills.map((skill, idx) => (
                          <span key={idx} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-700 text-sm font-semibold rounded-lg shadow-sm">
                             {skill}
                             <button type="button" onClick={() => removeSkill(skill)} className="text-gray-400 hover:text-red-500 transition-colors"><X className="w-3 h-3" /></button>
                          </span>
                        ))}
                      </div>
                   </div>
                 </div>
                 
                 {/* Visuals */}
                 <div className="space-y-4">
                   <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wider mb-4 border-b pb-2">Card Theme Color</h3>
                   
                   <div>
                      <div className="flex flex-wrap gap-3">
                        {presetColors.map((preset, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, color: preset.primary, colorSecondary: preset.secondary }))}
                            className={`w-12 h-12 rounded-xl transition-all hover:scale-110 shadow-sm ${
                              formData.color === preset.primary ? 'ring-2 ring-offset-2 ring-[#4C1D95] scale-110' : ''
                            }`}
                            style={{ background: `linear-gradient(135deg, ${preset.primary} 0%, ${preset.secondary} 100%)` }}
                            title={preset.name}
                          >
                            {formData.color === preset.primary && <Check className="w-6 h-6 text-white/90 mx-auto" />}
                          </button>
                        ))}
                      </div>
                   </div>
                 </div>

              </div>

              {/* Footer */}
              <div className="p-5 border-t bg-gray-50 flex gap-3 justify-end items-center px-8">
                <button 
                  type="button" 
                  onClick={closeModal} 
                  className="px-6 py-2.5 text-sm font-bold text-gray-500 hover:text-gray-700 transition-colors uppercase tracking-widest"
                >
                  Discard
                </button>
                <button 
                  type="submit" 
                  disabled={saving}
                  className="px-8 py-2.5 bg-[#4C1D95] text-white rounded-xl hover:bg-[#0d4543] transition-all hover:scale-[1.02] active:scale-[0.98] font-bold shadow-lg shadow-[#4C1D95]/20 flex items-center gap-2"
                >
                  {saving ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Check className="w-5 h-5" />
                      {editingExp ? 'Update Experience' : 'Save Experience'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
